import moment from "moment";
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { useCases } from "../../../utils/constants";
import ProcessIcon from "../../../assets/svg/processIcon";
import CopyIcon from "../../../assets/svg/copyIcon";
import ThumbsUpIcon from "../../../assets/svg/thumbsUpIcon";
import ThumbsDownIcon from "../../../assets/svg/thumbsDownIcon";
import ShareIcon from "../../../assets/svg/shareIcon";
import AdditionalContentIcon from "../../../assets/svg/additionalContentIcon";
import { FeedbackModal, ShareModal } from "../modal";
import { useChat } from "../../../context/chatContext";
import Alert from "../alerts/alert";
import { Alert as AlertType } from "../../../utils/interfaces";
import AdditionalContentSidebar from "./additionalContentSidebar";
import { useAuth } from "../../../context/authContext";
import { useNavigate } from "react-router-dom";

export interface MessageWrapProps {
	msg: any;
	icon?: boolean;
	showMessage: boolean;
	selectUseCase: (value: number) => void;
	setDisabled: (param: boolean) => void;
	hideIcons?: boolean;
}

const MessageWrap: React.FC<MessageWrapProps> = React.memo(
	({ msg, showMessage, selectUseCase, setDisabled, hideIcons }) => {

		// Add state for tracking copy status
		const [isCopied, setIsCopied] = useState(false);
		
		// Add state for feedback modal
		const [showFeedbackModal, setShowFeedbackModal] = useState(false);
		const [feedbackType, setFeedbackType] = useState<'positive' | 'negative'>('positive');
		
		// Add state for share modal
		const [showShareModal, setShowShareModal] = useState(false);
		
		// Add alert state
		const [alert, setAlert] = useState<AlertType | null>(null);
		
			// Add state for additional content sidebar
	const [showAdditionalContentSidebar, setShowAdditionalContentSidebar] = useState(false);
	const [additionalContentLoading, setAdditionalContentLoading] = useState(false);
	const [validationUrls, setValidationUrls] = useState<any[]>([]);
		
		// Get chatId from context
		const { chatId } = useChat();
		const { logout } = useAuth();
		const navigate = useNavigate();
		
		const formattedTime = moment(msg.time, "h:mm:ss A").format("h:mm A");
		
		// Handle alert messages
		const handleAlert = (message: string, type: "success" | "danger") => {
			setAlert({
				message,
				type,
				show: true
			});
		};
		useEffect(() => {
			if (msg.text.length == 0) {
				setDisabled(false);
			}
		}, [msg.text]);

		// Add this handler
		const handleCopy = async (text: string) => {
			try {
				// Clean the text before copying
				const cleanText = text
					.replace(/&nbsp;/g, " ") // Replace &nbsp; with regular space
					.replace(/\\n/g, "\n") // Replace literal \n with newline
					.replace(/\\t/g, "    ") // Replace literal \t with spaces
					.replace(/\\r/g, "") // Remove \r
					.trim();
				await navigator.clipboard.writeText(cleanText);
				setIsCopied(true);
			} catch (err) {
				console.error("Failed to copy text: ", err);
			} finally {
				// Reset the icon after 2 seconds
				setTimeout(() => {
					setIsCopied(false);
				}, 2000);
			}
		};

		// Add feedback handler
		const handleFeedback = (type: 'positive' | 'negative') => {
			setFeedbackType(type);
			setShowFeedbackModal(true);
		};

		// Add share handler
		const handleShare = () => {
			setShowShareModal(true);
		};

			// Add additional content handler
	const handleAdditionalContent = async () => {
		if (!msg.message_id) {
			handleAlert("Message ID not available", "danger");
			return;
		}

		setShowAdditionalContentSidebar(true);
		setAdditionalContentLoading(true);
		setValidationUrls([]);

		try {
			const token = localStorage.getItem('token');
			if (!token) {
				handleAlert("Authentication required", "danger");
				return;
			}

			const response = await fetch(
				process.env.REACT_APP_SERVER_URL + "/search_reference/by_message_id" ||
				"https://chat.1ciso.io/backend/search_reference/by_message_id",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						message_id: msg.message_id,
					}),
				}
			);

			if (response.status === 401 || response.status === 403) {
				logout();
				navigate("/");
				return;
			}

			if (!response.ok) {
				throw new Error("Failed to fetch additional content");
			}

			const responseData = await response.json();
			
			if (responseData.success && responseData.validation_urls) {
				setValidationUrls(responseData.validation_urls);
			} else {
				handleAlert("No additional content available", "danger");
			}
		} catch (error) {
			console.error("Error fetching additional content:", error);
			handleAlert("Failed to fetch additional content. Please try again.", "danger");
		} finally {
			setAdditionalContentLoading(false);
		}
	};

		

		// Add share submission handler
		const handleShareSubmit = async (email: string, subject: string, message: string) => {
			try {
				const token = localStorage.getItem('token');
				const response = await fetch(
					process.env.REACT_APP_SERVER_URL + "/share-via-email" ||
					"https://chat.1ciso.io/backend/share-via-email",
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${token}`,
						},
						body: JSON.stringify({
							chat_id: chatId,
							receiver_email: email,
							subject: subject,
							message: message,
						}),
					}
				);

				if (!response.ok) {
					handleAlert("Failed to share via email. Please try again.", "danger");
				}

				console.log("Share email sent successfully");
				handleAlert("Email shared successfully!", "success");
			} catch (error) {
				console.error("Error sharing via email:", error);
				handleAlert("Failed to share via email. Please try again.", "danger");
			}
		};

		// Add feedback submission handler
		const handleFeedbackSubmit = async (feedback: string, rating: 'positive' | 'negative') => {
			try {
				const token = localStorage.getItem('token');
				const response = await fetch(
					process.env.REACT_APP_SERVER_URL + "/feedback/submit" ||
					"https://chat.1ciso.io/backend/feedback/submit",
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${token}`,
						},
										body: JSON.stringify({
					message_id: msg.message_id, // This will need to be passed from the streaming response
					chat_id: chatId,
					feedback_type: rating,
					feedback_comments: feedback,
				}),
					}
				);

				if (!response.ok) {
					throw new Error("Failed to submit feedback");
				}

				console.log("Feedback submitted successfully");
				handleAlert("Feedback submitted successfully!", "success");
			} catch (error) {
				console.error("Error submitting feedback:", error);
				handleAlert("Failed to submit feedback. Please try again.", "danger");
			}
		};

		// State to track the number of headings to display
		const [headingsToShow, setHeadingsToShow] = useState(5);

		// Function to toggle the number of headings displayed
		const toggleHeadings = () => {
			setHeadingsToShow(totalHeadings);
		};
		// Function to get the truncated message based on headings
		const getTruncatedMessageByHeadings = (
			text: string,
			headingLimit: number
		) => {
			// Regular expression to match lines with bold text (headings)
			const headingRegex = /^\s*[-*\d.]*\s*\*\*[^*]+\*\*/gm;
			let match;
			let count = 0;
			let lastIndex = 0;

			while ((match = headingRegex.exec(text)) !== null) {
				count++;
				if (count > headingLimit) {
					break;
				}
				lastIndex = match.index;
			}

			return count > headingLimit ? text.slice(0, lastIndex) : text;
		};

		// Determine if "see more" should be shown
		// Count lines with bold text (headings like "**Text**" or "**Text:** explanation")
		const totalHeadings = (msg.text.match(/^\s*[-*\d.]*\s*\*\*[^*]+\*\*/gm) || []).length;
		const showSeeMore = totalHeadings > headingsToShow;

		return (
			<div className={`message-wrap ${msg.type}`}>
				<div className="d-flex flex-column">
					<div className="card overflow-hidden">
						<div className="card-body">
							<div className="d-flex msg-user-info">
								{msg?.typewriter ? (
									<div className="card-text nignt" key={msg.time}>
										<ReactMarkdown
											remarkPlugins={[remarkBreaks]}
											children={getTruncatedMessageByHeadings(
												msg.text,
												headingsToShow
											)}
											components={{
												li: ({ node, ...props }) => (
													<li style={{ marginBottom: "3%" }} {...props} />
												),
												p: ({ node, ...props }) => (
													<p className="card-text nignt" {...props} />
												),
											}}
										/>

										{showSeeMore && (
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													toggleHeadings();
												}}
												style={{ color: "#007bff", cursor: "pointer" }}
											>
												See more
											</a>
										)}
									</div>
								) : (
									<p className="card-text nignt">
										{!hideIcons && (msg.icon ||
											(msg.text.length == 0 && (
												<i className="bi bi-chat-dots pe-2 blinking-icon"></i>
											)))}

										<ReactMarkdown
											remarkPlugins={[remarkBreaks]}
											children={getTruncatedMessageByHeadings(
												msg.text,
												headingsToShow
											)}
											components={{
												li: ({ node, ...props }) => (
													<li style={{ marginBottom: "3%" }} {...props} />
												),
												p: ({ node, ...props }) => (
													<p className="card-text nignt" {...props} />
												),
											}}
										/>

										{showSeeMore && (
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													toggleHeadings();
												}}
												style={{ color: "#007bff", cursor: "pointer" }}
											>
												See more
											</a>
										)}
										{showMessage && msg.type == "left" && (
											<p className="card-text nignt">
												<a
													href="#"
													onClick={() => {
														selectUseCase(useCases[0].value);
													}}
												>
													{useCases[0].name}
												</a>
												<br />
												<a
													href="#"
													onClick={() => {
														selectUseCase(useCases[1].value);
													}}
												>
													{useCases[1].name}
												</a>

												<br />
												<a
													href="#"
													onClick={() => {
														selectUseCase(useCases[2].value);
													}}
												>
													{useCases[2].name}
												</a>
												<br />
												<a
													href="#"
													onClick={() => {
														selectUseCase(useCases[3].value);
													}}
												>
													{useCases[3].name}
												</a>
											</p>
										)}
									</p>
								)}
							</div>
						</div>
						{msg.text &&
							msg.type === "left" && 
							<div className="card-footer d-flex justify-content-end px-4 pb-4 mx-3 bg-white">
								<div className="d-flex align-items-center gap-2">

									{/* Additional Content Button */}
									{msg.message_id && (
										<button
											className="btn btn-link"
											onClick={!hideIcons ? handleAdditionalContent : () => {}}
											style={{
												backgroundColor: "transparent",
												border: "none",
												padding: "0",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												opacity: hideIcons ? 0.5 : 1,
												pointerEvents: hideIcons ? "none" : "auto"
											}}
											title="View additional content"
										>
											<AdditionalContentIcon />
										</button>
									)}
									
									{/* Copy Button */}
									{!isCopied ? (
										<button
											className="btn btn-link"
											onClick={!hideIcons ? () => handleCopy(msg.text) : () => {}}
											style={{
												backgroundColor: "transparent",
												border: "none",
												// width: "23px",
												// height: "27px",
												padding: "0",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												opacity: hideIcons ? 0.5 : 1,
												pointerEvents: hideIcons ? "none" : "auto"
											}}
											title="Copy to clipboard"
										>
											<CopyIcon />
										</button>
									) : (
										<i
											className={`bi bi-check2 bottom-0 p-2 cursor-pointer`}
											style={{
												// width: "23px", 
												// height: "27px",
												fontSize: "18px",
												fontWeight: 500,
												paddingTop: "10px",
												transition: "all 0.2s ease",
												color: "#9eccff",
											}}
										/>
									)}

									{/* Share Button */}
									<button
										className="btn btn-link"
										onClick={!hideIcons ? handleShare : () => {}}
										style={{
											backgroundColor: "transparent",
											border: "none",
											padding: "0",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											opacity: hideIcons ? 0.5 : 1,
											pointerEvents: hideIcons ? "none" : "auto"
										}}
										title="Share via email"
									>
										<ShareIcon />
									</button>

									{/* Thumbs Up Button */}
									<button
										className="btn btn-link"
										onClick={!hideIcons ? () => handleFeedback('positive') : () => {}}
										style={{
											backgroundColor: "transparent",
											border: "none",
											// width: "23px",
											// height: "27px",
											padding: "0",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											opacity: hideIcons ? 0.5 : 1,
											pointerEvents: hideIcons ? "none" : "auto"
										}}
										title="Good response"
									>
										<ThumbsUpIcon />
									</button>

									{/* Thumbs Down Button */}
									<button
										className="btn btn-link"
										onClick={!hideIcons ? () => handleFeedback('negative') : () => {}}
										style={{
											backgroundColor: "transparent",
											border: "none",
											// width: "23px",
											// height: "27px",
											padding: "0",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											opacity: hideIcons ? 0.5 : 1,
											pointerEvents: hideIcons ? "none" : "auto"
										}}
										title="Poor response"
									>
										<ThumbsDownIcon />
									</button>
								</div>
							</div>
						}
					</div>
					
					{/* <small className="text-muted datetime">{formattedTime}</small> */}
					{!hideIcons && msg.text && msg.type === "left" && 
						<div className="d-flex align-items-center background-color-white datetime" style={{
							width: "30px",
							height: "30px",
							borderRadius: "50%",
							border: "1px solid #000",
							backgroundColor: "white",
						}}>
							<ProcessIcon />
						</div>
					}
				</div>
				
				{/* Feedback Modal */}
				<FeedbackModal
					show={showFeedbackModal}
					onClose={() => setShowFeedbackModal(false)}
					onSubmit={handleFeedbackSubmit}
					rating={feedbackType}
					title={feedbackType === 'positive' ? 'Tell us what you liked' : 'Tell us what went wrong'}
				/>
				
				{/* Share Modal */}
				<ShareModal
					show={showShareModal}
					onClose={() => setShowShareModal(false)}
					onSubmit={handleShareSubmit}
					title="Share Chat via Email"
				/>
				
				{/* Additional Content Sidebar */}
				<AdditionalContentSidebar
					isOpen={showAdditionalContentSidebar}
					onClose={() => setShowAdditionalContentSidebar(false)}
					messageId={msg.message_id || ""}
					loading={additionalContentLoading}
					validationUrls={validationUrls}
				/>
				
				{/* Alert Component */}
				{alert && (
					<Alert
						message={alert.message}
						type={alert.type}
						show={alert.show}
						onClose={() => setAlert(null)}
					/>
				)}
			</div>
		);
	}
);

export default MessageWrap;
